import { type TagVariant, type IconName } from '@dev-spendesk/grapes';

import { type TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import type { RequestAPI } from 'modules/requests/types';
import { formatMoney } from 'src/core/utils/money';

type RawPayment = {
  id: string;
  missingReceipt?: {
    type: string;
    user?: { fullname: string };
    message: string;
  };
  card?: { type: 'single_purchase' | 'subscription' | 'physical' };
  card_id?: string;
  request?: Pick<RequestAPI, 'type'>;
  invoices?: { total: number };
  invoice_lost?: boolean;
  invoice_invalid?: boolean;
  completionState?: 'complete' | 'incomplete';
  completionDeadline?: {
    daysLeft: number;
    status: 'late' | 'urgent' | 'onTime';
  };
  is_atm_withdrawal?: boolean;
  supplier?: { name: string; url: string | null; id?: string };
};

export const shouldShowDeleteInvoiceTooltip = (
  payment: Pick<RawPayment, 'missingReceipt' | 'request'>,
  invoices: unknown[],
) => {
  const { missingReceipt } = payment;

  return (
    (isSinglePurchase(payment) ||
      isPhysicalCardPurchase(payment) ||
      isMultiUseCardPurchase(payment) ||
      isSubscription(payment)) &&
    invoices.length === 1 &&
    !missingReceipt
  );
};

const isSinglePurchase = (payment: Pick<RawPayment, 'request'>) =>
  payment.request?.type === 'single_purchase';

const isSubscription = (payment: Pick<RawPayment, 'request'>) =>
  payment.request?.type === 'subscription';

const isMultiUseCardPurchase = (payment: Pick<RawPayment, 'request'>) =>
  payment.request?.type === 'multi_use_card';

const isPhysicalCardPurchase = (
  payment: Pick<RawPayment, 'request' | 'card'>,
) => payment.card?.type === 'physical';

const isMileageAllowance = (payment: Pick<RawPayment, 'request'>) =>
  payment.request?.type === 'mileage_allowance';

export const isPerDiemAllowance = (payment: Pick<RawPayment, 'request'>) =>
  payment.request?.type === 'per_diem_allowance';

const hasLostInvoice = (
  payment: Pick<RawPayment, 'invoices' | 'invoice_lost'>,
) => {
  return payment.invoice_lost ?? false;
};

const hasNoInvoice = (payment: Pick<RawPayment, 'invoices'>) => {
  return !payment.invoices || payment.invoices.total === 0;
};

const hasMissingInformation = (payment: RawPayment) => {
  // A payment is considered to have missing information if:
  // - it is incomplete from a PBR standpoint (i.e., it has some missing information and it's counted against PBR)
  // - it has no invoice associated with it even if it is complete from a PBR standpoint
  // The latter can happen because from a PBR standpoint, whenever the payable associated to a payment is accepted as is
  // by the accountant, it no longer counts against PBR - however, that does not mean that the payment has an invoice associated with it.
  return payment.completionState === 'incomplete' || hasNoInvoice(payment);
};

export const hasInvalidInvoice = (
  payment: Pick<RawPayment, 'invoice_invalid'>,
) => payment.invoice_invalid;

export const getTagDataFromPayment = (
  payment: RawPayment,
  translator: TGlobalFunctionTyped,
): {
  icon: IconName;
  variant: TagVariant;
  content: string;
} => {
  if (isMileageAllowance(payment) || isPerDiemAllowance(payment)) {
    return {
      icon: 'circle-check',
      variant: 'success',
      content: translator('payments.status.provided'),
    };
  }

  if (hasInvalidInvoice(payment)) {
    return {
      icon: 'triangle-warning',
      variant: 'warning',
      content: translator('payments.status.invalid'),
    };
  }

  // "lost invoice" is legacy status
  // we keep it for backward compatibility on old payments
  if (hasLostInvoice(payment)) {
    return {
      icon: 'circle-question-filled',
      variant: 'neutral',
      content: translator('payments.status.lost'),
    };
  }

  if (hasMissingInformation(payment)) {
    return {
      icon: 'triangle-warning',
      variant: 'warning',
      content: translator('payments.status.incomplete'),
    };
  }

  return {
    icon: 'circle-check',
    variant: 'success',
    content: translator('payments.status.provided'),
  };
};

export const getPaymentFormattedAmounts = (
  payment: {
    amount_declared: number;
    amount_billed: number;
    currency_declared: string;
    fx_fee_amount: number;
  },
  companyCurrency: string,
) => {
  const {
    amount_declared: amountDeclared,
    amount_billed: amountBilled,
    currency_declared: currencyDeclared,
    fx_fee_amount: fxFeeAmount,
  } = payment;

  const totalAmountBilled = amountBilled + fxFeeAmount;

  const amountInCompanyCurrency =
    totalAmountBilled < 0
      ? formatMoney(Math.abs(totalAmountBilled), companyCurrency, true)
      : formatMoney(totalAmountBilled, companyCurrency);

  let amountInExpenditureCurrency;
  if (companyCurrency !== currencyDeclared) {
    amountInExpenditureCurrency =
      amountDeclared < 0
        ? formatMoney(Math.abs(amountDeclared), currencyDeclared, true)
        : formatMoney(amountDeclared, currencyDeclared);
  }

  const feesAmountInCompanyCurrency =
    fxFeeAmount > 0 ? formatMoney(fxFeeAmount, companyCurrency) : undefined;

  return {
    amountInCompanyCurrency,
    amountInExpenditureCurrency,
    feesAmountInCompanyCurrency,
  };
};
